import React from 'react';
import { CheckCircle2, XCircle, UsersRound } from 'lucide-react';
import { Player } from '../../types/Player';
import { Coach } from '../../types/Coach'; // Keep Coach import if needed elsewhere, but not used for icons
import { findProperAgeGroup, AgeGroupWithRange } from '../../lib/ageGroups';
import JerseyBadge from './JerseyBadge';

interface PlayerListProps {
  players: Player[];
  selectedPlayer: Player | null;
  onSelect: (playerId: string) => void;
  coachId: string;
  coaches: Coach[];
  allAgeGroups: AgeGroupWithRange[];
  onRefresh: () => void; // New prop for triggering refresh
}

// isEvaluationComplete function removed as completeness is not checked here anymore

import { useEffect, useRef, useState } from 'react';

const REFRESH_OPTIONS = [
  { label: '1 Min', value: 60_000 },
  { label: '2 Min', value: 120_000 },
  { label: '5 Min', value: 300_000 },
  { label: 'Never', value: 0 },
];

const PlayerList: React.FC<PlayerListProps> = ({ players, selectedPlayer, onSelect, coachId, coaches, allAgeGroups, onRefresh }) => {
  const [refreshInterval, setRefreshInterval] = useState<number>(120_000); // Default 2 Min
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle interval changes
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(() => {
        onRefresh();
      }, refreshInterval);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refreshInterval, onRefresh]);

  // Refresh state: 'idle' | 'refreshing' | 'refreshed'
  const [refreshState, setRefreshState] = useState<'idle' | 'refreshing' | 'refreshed'>('idle');
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleRefreshNow = () => {
    if (refreshState !== 'idle') return; // Prevent multiple triggers
    setRefreshState('refreshing');
    Promise.resolve(onRefresh())
      .then(() => {
        setRefreshState('refreshed');
        refreshTimeoutRef.current = setTimeout(() => {
          setRefreshState('idle');
        }, 3000);
      })
      .catch(() => {
        setRefreshState('idle');
      });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const sortedPlayers = players.sort((a, b) => parseInt(a.jerseyNumber || '0') - parseInt(b.jerseyNumber || '0'));

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          Refresh Interval:
          <select
            className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={refreshInterval}
            onChange={e => setRefreshInterval(Number(e.target.value))}
          >
            {REFRESH_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        {refreshState === 'idle' && (
          <button
            className="ml-2 px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition text-sm"
            onClick={handleRefreshNow}
            type="button"
          >
            Refresh Now
          </button>
        )}
        {refreshState === 'refreshing' && (
          <span className="ml-2 text-blue-500 font-medium">Refreshing...</span>
        )}
        {refreshState === 'refreshed' && (
          <span className="ml-2 text-green-600 font-medium">Refreshed</span>
        )}
      </div>
      <div className="mt-6 space-y-2 max-h-[calc(100vh-20rem)] overflow-y-auto">
        {sortedPlayers.map(player => {
          const coachIdsWhoEvaluated = player.evaluatedByCoachIds || [];
          const hasMyEvaluation = coachIdsWhoEvaluated.includes(coachId);
          const hasOtherEvaluation = coachIdsWhoEvaluated.some(id => id !== coachId);

          const fullName = `${player.first_name ?? ''} ${player.last_name ?? ''}`.trim();
          const properAgeGroup = findProperAgeGroup(player.birthdate, player.sex, allAgeGroups);
          const positionsAndTeams = (player.profile?.positions ?? []).concat(player.profile?.competitiveness ?? []).join(', ');
          const secondLine = [properAgeGroup?.group_name, positionsAndTeams].filter(Boolean).join(' • ');
          return (
            <button
              key={player.id}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                onSelect(player.id);
              }}
              className={`w-full text-left border ${
                selectedPlayer?.id === player.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              } rounded-lg p-4 transition`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <JerseyBadge number={player.jerseyNumber} color={player.jerseyColor} size="md" />
                  <div>
                    <h3 className="font-medium">{fullName}</h3>
                    <p className="text-sm text-gray-600">
                      {secondLine}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {hasOtherEvaluation && (
                    <UsersRound className="h-5 w-5 text-blue-500" title="Evaluated by other coaches" />
                  )}
                  {hasMyEvaluation ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" title="Evaluated by you" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-300" title="Not evaluated by you" />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PlayerList;